import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bed, Bathtub, UsersThree, MapPin, ArrowLeft, CheckCircle, ArrowUpRight, Star, NavigationArrow, Quotes, Barbell, CalendarBlank, SwimmingPool, PlugCharging, CaretDown } from "@phosphor-icons/react";
import { getProperty, properties } from "@/data/properties";
import { reviewsForProperty } from "@/data/propertyReviews";
import { propertyFaq } from "@/data/propertyFaq";
import PropertyGallery from "@/components/site/PropertyGallery";
import { HospitableWidget } from "@/components/site/HospitableWidget";
import { BookDirectSave } from "@/components/site/BookDirectSave";
import { EvChargerDetails } from "@/components/site/EvChargerDetails";
import Seo, { SITE } from "@/components/site/Seo";
import { propertyJsonLd, breadcrumbJsonLd, faqJsonLd } from "@/lib/schema";
import { trackBookingIntent } from "@/lib/analytics";

export default function PropertyDetail() {
  const { slug } = useParams();
  const property = getProperty(slug);
  const others = properties.filter((p) => p.slug !== slug).slice(0, 2);
  const reviews = reviewsForProperty(slug);
  const propFaqs = property ? propertyFaq(property) : [];
  const directionsUrl = property?.coords
    ? `https://www.google.com/maps/dir/?api=1&destination=${property.coords[0]},${property.coords[1]}`
    : null;

  // Dates carried through from the events calendar / booking search.
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const ci = params.get("checkin");
  const co = params.get("checkout");
  const hasDates = Boolean(ci && co);
  const fmtD = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  // Sticky reserve bar: appears once the hero scrolls away, hides while the
  // booking widget (#book) is on screen so it never duplicates it.
  const [pastHero, setPastHero] = useState(false);
  const [bookVisible, setBookVisible] = useState(false);
  const [evOpen, setEvOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > 520);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    const bookEl = document.getElementById("book");
    const io = new IntersectionObserver(([e]) => setBookVisible(e.isIntersecting), { threshold: 0.05 });
    if (bookEl) io.observe(bookEl);
    return () => {
      window.removeEventListener("scroll", onScroll);
      io.disconnect();
    };
  }, [property.slug]);
  const showReserve = pastHero && !bookVisible;
  const scrollToBook = () => {
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Deep-link support: /properties/:slug#book scrolls to the live booking widget
  // (runs after the app's ScrollToTop reset and initial render).
  useEffect(() => {
    if (window.location.hash === "#book") {
      const t = setTimeout(() => {
        document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 450);
      return () => clearTimeout(t);
    }
  }, [slug]);

  if (!property) {
    return (
      <main className="pt-40 pb-32 max-w-[1400px] mx-auto px-6 md:px-10 text-center">
        <h1 className="font-display text-5xl mb-6">Stay not found</h1>
        <Link to="/properties" className="text-sunset hover:underline">Back to all stays</Link>
      </main>
    );
  }

  return (
    <main className="pt-28 md:pt-32">
      <Seo
        title={`${property.name} | Double Rainbow Suites`}
        description={`${property.tagline} ${property.location} vacation rental sleeping ${property.guests}, ${property.bedrooms} bedroom. Book instantly with Double Rainbow Suites.`}
        canonical={`${SITE}/properties/${property.slug}`}
        image={property.image}
        jsonLd={[
          propertyJsonLd(property, reviews),
          faqJsonLd(propFaqs),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Stays", path: "/properties" },
            { name: property.shortName || property.name, path: `/properties/${property.slug}` },
          ]),
        ]}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link to="/properties" data-testid="back-to-properties" className="inline-flex items-center gap-2 text-emerald-soft hover:text-sunset transition-colors duration-300 mb-8">
          <ArrowLeft size={18} /> All stays
        </Link>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-emerald-soft mb-4">
            <span className="flex items-center gap-2"><MapPin size={15} weight="fill" className="text-sunset" /> {property.location}</span>
            {property.rating && (
              <span className="flex items-center gap-1.5 text-emerald"><Star size={15} weight="fill" className="text-gold" /> {property.rating.toFixed(1)} · {property.reviewCount} reviews</span>
            )}
            {property.guestFavourite && (
              <span className="rounded-full bg-sunset/10 text-sunset px-2.5 py-0.5 text-xs font-semibold">Guest favorite</span>
            )}
          </div>
          <h1 data-testid="property-title" className="font-display text-4xl md:text-7xl leading-[0.9] tracking-tight max-w-4xl">
            {property.name}
          </h1>
          <p className="text-lg md:text-xl text-emerald-soft max-w-2xl mt-6 font-light">{property.tagline}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12"
        >
          <PropertyGallery images={property.gallery || [property.image]} name={property.name} />
        </motion.div>
      </div>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-[1.5fr_1fr] gap-14">
        <div>
          <div className="flex flex-wrap gap-x-10 gap-y-4 pb-10 border-b border-emerald/10">
            <span className="flex items-center gap-2 text-emerald"><UsersThree size={22} className="text-sunset" /> {property.guests} guests</span>
            <span className="flex items-center gap-2 text-emerald"><Bed size={22} className="text-sunset" /> {property.bedrooms} bedroom{property.bedrooms > 1 ? "s" : ""}</span>
            <span className="flex items-center gap-2 text-emerald"><Bathtub size={22} className="text-sunset" /> {property.bathrooms} bathroom{property.bathrooms > 1 ? "s" : ""}</span>
          </div>

          <h2 className="font-display text-3xl md:text-4xl mt-10 mb-5">About this stay</h2>
          {property.localIntro && (
            <p data-testid="property-local-intro" className="font-display text-xl md:text-2xl leading-snug text-emerald mb-6">
              {property.localIntro}
            </p>
          )}
          <p className="text-emerald-soft text-lg leading-relaxed font-light">{property.description}</p>

          <h2 className="font-display text-3xl md:text-4xl mt-14 mb-6">What's included</h2>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {property.amenities.map((a) => (
              <div key={a} className="flex items-center gap-3 text-emerald">
                <CheckCircle size={20} weight="fill" className="text-sage" /> {a}
              </div>
            ))}
          </div>
          <div data-testid="crunch-fitness-perk" className="mt-6 flex items-center gap-3 rounded-2xl border border-sunset/25 bg-sunset/10 px-5 py-4">
            <span className="w-10 h-10 rounded-full bg-sunset/15 grid place-items-center shrink-0">
              <Barbell size={22} weight="fill" className="text-sunset" />
            </span>
            <p className="text-emerald font-medium">
              <span className="text-sunset font-semibold">FREE</span> access to CRUNCH Fitness during your stay
            </p>
          </div>
          {(property.amenities || []).some((a) => /EV charger/i.test(a)) && (
            <div className="mt-4">
              <button
                type="button"
                data-testid="ev-charger-perk-toggle"
                aria-expanded={evOpen}
                aria-label="Show EV charger details"
                onClick={() => setEvOpen((o) => !o)}
                className="w-full flex items-center gap-3 rounded-2xl border border-amber-400/60 bg-amber-400/10 px-5 py-4 text-left transition-colors duration-300 hover:bg-amber-400/20"
              >
                <span className="w-10 h-10 rounded-full bg-amber-400/25 grid place-items-center shrink-0">
                  <PlugCharging size={22} weight="fill" className="text-amber-500" />
                </span>
                <p className="text-emerald font-medium flex-1">
                  <span className="text-amber-600 font-semibold">FREE</span> Level 2 EV charging during your stay
                </p>
                <CaretDown size={18} weight="bold" className={`text-amber-600 shrink-0 transition-transform duration-300 ${evOpen ? "rotate-180" : ""}`} />
              </button>
              <motion.div
                initial={false}
                animate={{ height: evOpen ? "auto" : 0, opacity: evOpen ? 1 : 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <div data-testid="ev-charger-perk-panel" className="pt-3">
                  <EvChargerDetails />
                </div>
              </motion.div>
            </div>
          )}
          {property.comingSoon && (
            <div data-testid="coming-soon-note" className="mt-4 flex items-center gap-3 rounded-2xl border border-emerald/15 bg-emerald/5 px-5 py-4">
              <span className="w-10 h-10 rounded-full bg-emerald/10 grid place-items-center shrink-0">
                <SwimmingPool size={22} weight="fill" className="text-emerald" />
              </span>
              <p className="text-emerald-soft text-sm leading-relaxed">
                <span className="text-emerald font-semibold">Coming Fall 2026</span> — {property.comingSoon}
              </p>
            </div>
          )}
        </div>

        {/* Booking sidebar — Hospitable Direct */}
        <aside className="lg:sticky lg:top-28 h-fit">
          <div data-testid="property-booking-box" className="border border-emerald/10 rounded-2xl p-8 bg-sand-2">
            <p className="overline text-sunset mb-3">Reserve</p>
            <p className="font-display text-3xl leading-tight mb-2">Book instantly</p>
            <p className="text-emerald-soft text-sm mb-6">Check live rates and availability below and reserve securely through Hospitable.</p>
            {hasDates && (
              <div data-testid="property-selected-dates" className="mb-5 flex items-start gap-2 rounded-xl bg-sunset/10 border border-sunset/25 px-3.5 py-2.5 text-sm text-emerald">
                <CalendarBlank size={16} weight="fill" className="text-sunset shrink-0 mt-0.5" />
                <span>Your dates: <span className="font-semibold">{fmtD(ci)} → {fmtD(co)}</span> — already pre-selected in the calendar below.</span>
              </div>
            )}
            <a
              data-testid="property-book-btn"
              href="#book"
              onClick={() => trackBookingIntent(property)}
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-sunset px-6 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald"
            >
              Check dates <ArrowUpRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <div className="mt-5 pt-5 border-t border-emerald/10 flex flex-col gap-3 text-center">
              {directionsUrl && (
                <a
                  data-testid="property-directions-btn"
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full border border-emerald/20 px-6 py-3 text-sm font-medium text-emerald transition-colors duration-300 hover:bg-emerald hover:text-sand"
                >
                  <NavigationArrow size={16} weight="fill" className="text-sunset group-hover:text-gold transition-colors duration-300" /> Get directions
                </a>
              )}
              <Link to="/contact" className="text-sm text-emerald hover:text-sunset transition-colors duration-300">Have a question? Contact us →</Link>
            </div>
          </div>
        </aside>
      </section>

      {/* Book direct vs platforms comparison */}
      <section data-testid="book-direct-save-section" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <BookDirectSave onCta={() => { trackBookingIntent(property); scrollToBook(); }} />
      </section>

      {/* Hospitable booking widget */}
      <section id="book" data-testid="property-booking-widget" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24 scroll-mt-28">
        <div className="mb-8">
          <p className="overline text-sunset mb-3">Availability & booking</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight">Reserve your dates</h2>
        </div>
        <div className="rounded-2xl overflow-hidden border border-emerald/10 bg-sand p-4 md:p-8">
          {/* key on dates: a new search must reload the calendar with the new URL dates */}
          <HospitableWidget key={`${property.widgetId}-${ci || ""}-${co || ""}`} propertyId={property.widgetId} propertyName={property.name} />
        </div>
      </section>

      {/* Guest reviews */}
      {reviews.length > 0 && (
        <section data-testid="property-reviews" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-8 md:pb-16">
          <div className="flex items-center gap-3 mb-10">
            <Star size={24} weight="fill" className="text-gold" />
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">
              What guests say
            </h2>
            {property.rating && (
              <span className="ml-2 text-emerald-soft text-lg">· {property.rating.toFixed(1)} average</span>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {reviews.map((r, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="relative border border-emerald/10 rounded-2xl p-7 bg-sand-2"
                data-testid={`property-review-${i}`}
              >
                <Quotes size={30} weight="fill" className="text-sunset/25 mb-3" />
                <div className="text-sunset text-sm mb-4">{"★".repeat(Math.round(r.rating))}</div>
                <blockquote className="font-display text-xl leading-snug text-emerald mb-6">"{r.text}"</blockquote>
                <figcaption className="text-sm text-emerald-soft">
                  <span className="font-medium text-emerald">{r.name}</span> · {r.date}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </section>
      )}

      {/* Good to know — per-property quotable answers */}
      {propFaqs.length > 0 && (
        <section data-testid="property-faq" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-8 md:pb-16">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-10">Good to know</h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
            {propFaqs.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.06 }}
                className="border-t border-emerald/12 pt-6"
                data-testid={`property-faq-${i}`}
              >
                <h3 className="font-display text-2xl text-emerald mb-3 leading-snug">{f.q}</h3>
                <p className="text-emerald-soft leading-relaxed font-light">{f.a}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Other stays */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-10">More places to stay</h2>
        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {others.map((p, i) => (
            <PropertyCardMini key={p.slug} property={p} index={i} />
          ))}
        </div>
      </section>

      {/* Sticky reserve bar */}
      <div
        data-testid="sticky-reserve-bar"
        aria-hidden={!showReserve}
        className={`fixed bottom-4 inset-x-0 z-40 flex justify-center px-4 pointer-events-none transition-all duration-500 ${
          showReserve ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
        }`}
      >
        <div className="pointer-events-auto flex items-center gap-4 rounded-full bg-emerald text-sand shadow-2xl pl-6 pr-2 py-2 max-w-full border border-sand/10">
          <div className="min-w-0 hidden sm:block">
            <p className="font-display text-base leading-tight truncate">{property.shortName}</p>
            {hasDates && (
              <p className="text-sand/60 text-xs leading-tight mt-0.5" data-testid="sticky-reserve-dates">
                Your dates: {fmtD(ci)} → {fmtD(co)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={scrollToBook}
            data-testid="sticky-reserve-button"
            className="rounded-full bg-sunset px-6 py-3 text-sm font-medium text-sand transition-colors duration-300 hover:bg-gold hover:text-emerald shrink-0"
          >
            {hasDates ? "Reserve — your dates" : "Check availability"}
          </button>
        </div>
      </div>
    </main>
  );
}

function PropertyCardMini({ property, index }) {
  return (
    <Link to={`/properties/${property.slug}`} data-testid={`related-${property.slug}`} className="group block relative overflow-hidden rounded-2xl aspect-[16/9]">
      <img src={property.image} alt={property.name} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-emerald/70 to-transparent" />
      <div className="absolute bottom-0 p-6 text-sand">
        <h3 className="font-display text-2xl">{property.name}</h3>
        <p className="text-sand/80 text-sm mt-1">{property.location}</p>
      </div>
    </Link>
  );
}
